import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../App.jsx';
import authService from '../services/authService.js';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

/**
 * LoginModal — Modal de inicio de sesión email + contraseña.
 *
 * Se cierran automáticamente tras un login exitoso.
 * El contexto de Auth almacena el token y los datos del usuario.
 */
export default function LoginModal({ onClose }) {
  const { login } = useAuth();
  const [view, setView] = useState('login'); // 'login' | 'forgot'
  const [form, setForm] = useState({ email: '', password: '' });
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!executeRecaptcha) {
      setError('ReCAPTCHA no está listo aún.');
      return;
    }

    try {
      const recaptchaToken = await executeRecaptcha('login');
      setError('');
      setLoading(true);

      const { token, user } = await authService.login(form.email, form.password, recaptchaToken);
      login({ ...user, token });
      toast.success(`¡Bienvenido, ${user.name}!`);
      onClose();
      
      if (['super_admin', 'admin', 'boss', 'staff'].includes(user.role)) {
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 300);
      }
    } catch (err) {
      const msg =
        err.response?.data?.errors?.email?.[0] ||
        err.response?.data?.message ||
        'Error al iniciar sesión. Verifica tus credenciales.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!executeRecaptcha) {
      setError('ReCAPTCHA no está listo aún.');
      return;
    }

    try {
      const recaptchaToken = await executeRecaptcha('forgot_password');
      setError('');
      setSuccess('');
      setLoading(true);

      const res = await authService.forgotPassword(resetEmail, recaptchaToken);
      setSuccess(res.message);
      toast.success('Solicitud enviada correctamanente');
      setResetEmail('');
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al procesar la solicitud.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <div>
            <h3 style={{ margin: 0 }}>{view === 'login' ? 'Iniciar Sesión' : 'Recuperar Contraseña'}</h3>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: 4 }}>
              CBR Digital · Repositorio Registral
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        {view === 'login' ? (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="login-email">Correo electrónico</label>
              <input
                id="login-email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="usuario@cbr.cl"
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label htmlFor="login-password">Contraseña</label>
                <button type="button" onClick={() => { setView('forgot'); setError(''); setSuccess(''); }} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}>
                  ¿Olvidó su contraseña?
                </button>
              </div>
              <input
                id="login-password"
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                ⚠️ {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center' }}
              disabled={loading}
            >
              {loading ? <span className="spinner" /> : '🔑 Ingresar'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleForgotSubmit}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Ingrese su correo electrónico y le enviaremos un enlace seguro para restablecer su contraseña.
            </p>
            <div className="form-group">
              <label htmlFor="reset-email">Correo electrónico</label>
              <input
                id="reset-email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="usuario@cbr.cl"
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                ⚠️ {error}
              </div>
            )}
            {success && (
              <div className="alert alert-success" style={{ marginBottom: '1rem', background: 'var(--success-bg)', color: 'var(--success)', padding: '10px', borderRadius: '8px', border: '1px solid var(--success)' }}>
                ✅ {success}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginBottom: '1rem' }}
              disabled={loading}
            >
              {loading ? <span className="spinner" /> : '📩 Enviar Enlace'}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => { setView('login'); setError(''); setSuccess(''); }}
              disabled={loading}
            >
              Volver al Login
            </button>
          </form>
        )}

        <p className="text-muted" style={{ fontSize: '0.78rem', textAlign: 'center', marginTop: '1.2rem' }}>
          🔒 Autenticación segura · Integración ClaveÚnica disponible próximamente
        </p>
      </div>
    </div>
  );
}
